import { Component, computed, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { User } from 'shared/types/user.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, tap } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RemoveAccountModalComponent } from '../../okeey-cash-account-modal/remove-account-modal/remove-account-modal.component';
import { TagComponent } from 'shared/components/tag/tag.component';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NgTemplateOutlet } from '@angular/common';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { FormsModule } from '@angular/forms';
import { UsersService } from 'core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DeleteAccountComponent } from '../../delete-account/delete-account.component';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TaxDocumentationModalComponent } from '../../tax-documentation-modal/tax-documentation-modal.component';
import { MaskPipe } from 'shared';
import { Account, TaxDocument } from 'shared/types/payment-account.interface';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { NetworkError } from 'shared/models/network-error';
import { HideTooltipOnScrollDirective } from 'shared/directives';
import { RemoveTaxDocumentationModalComponent } from '../../remove-tax-documentation-modal/remove-tax-documentation-modal.component';
import { ContactUsModalComponent } from '../../../../shared/modal/contact-us-modal/contact-us-modal.component';

enum OkeeyCashAccountNumberStatuses {
  APPROVED = 'APPROVED',
  REVIEW = 'REVIEW',
  REVIEWING = 'REVIEWING',
  DECLINED = 'DECLINED'
}

@Component({
  selector: 'app-personal-information-tab',
  imports: [
    TranslatePipe,
    TagComponent,
    NzButtonComponent,
    NgTemplateOutlet,
    NzRadioComponent,
    NzRadioGroupComponent,
    FormsModule,
    RouterLink,
    NzPopconfirmDirective,
    NzTooltipDirective,
    HideTooltipOnScrollDirective,
    MaskPipe
  ],
  templateUrl: './personal-information-tab.component.html',
  styleUrl: './personal-information-tab.component.scss'
})
export class PersonalInformationTabComponent {
  #modalService = inject(NzModalService);
  #translateService = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #usersService = inject(UsersService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  #paymentAccountsService = inject(PaymentAccountsService);

  user = input<User>();
  paymentAccounts = input<Account[]>();
  readonly okeeyCashAccountRemoved = output<void>();

  readonly okeeyCashAccountStatus = computed(() => this.user()?.okeeyCashAccountNumberStatusCode);
  readonly OkeeyCashAccountNumberStatuses = OkeeyCashAccountNumberStatuses;

  userAddress = computed(() => {
    return [this.user()?.streetAddress, this.user()?.address, this.user()?.zipCode].filter(Boolean).join(', ');
  });

  public paymentAccountStatusIsEmpty = computed<boolean>(() => {
    return !this.paymentAccounts()?.length;
  });

  public paymentAccountStatusIsDeclined = computed<boolean>(() => {
    return this.paymentAccounts()?.some((a: Account) => a.overall_status === 'DECLINED');
  });
  public paymentAccountStatusIsReviewing = computed<boolean>(() => {
    return this.paymentAccounts()?.some((a: Account) => a.overall_status === 'REVIEWING');
  });
  public defaultPaymentAccount = computed<Account>(() => {
    return this.paymentAccounts()?.find((a: Account) => {
      return (
        (!!a.default || a.overall_status === 'APPROVED') &&
        a.overall_status !== 'REVIEWING' &&
        a.overall_status !== 'DECLINED'
      );
    });
  });

  public language: string;
  public isTaxDocumentationLoading = signal<boolean>(false);
  public taxDocument = signal<TaxDocument | null>(null);
  private taxDocumentationLoaded = false;
  public hasTaxDocumentation = computed<boolean>(() => !!this.taxDocument()?.id);

  sections = computed(() => {
    const sections = [{ id: 0, label: 'my-forest.userID', value: this.user()?.id?.toString() }];

    if (this.user()?.isSpecial) {
      sections.push({ id: 1, label: 'referral.contactName', value: this.user().contactName });
    }

    sections.push(
      { id: 2, label: 'my-forest.email', value: this.user().email },
      { id: 3, label: 'my-forest.phoneNumber', value: this.formatPhoneNumber() },
      { id: 4, label: 'my-forest.location', value: this.userAddress() },
      { id: 5, label: 'my-forest.referredBy', value: this.user()?.referredByFullName }
    );

    return sections;
  });

  constructor() {
    effect(() => {
      const languageCode = this.user()?.userLanguageCode;

      untracked(() => {
        this.language = languageCode;
      });
    });

    effect(() => {
      const isSpecial = this.user()?.isSpecial;

      if (isSpecial === false && !this.taxDocumentationLoaded) {
        this.taxDocumentationLoaded = true;
        this.loadTaxDocumentation();
      }
    });
  }

  public onContractContactClick(): void {
    this.#modalService.create({
      nzContent: ContactUsModalComponent,
      nzData: { initialQuestion: this.#translateService.instant('profile.contractAccessRequest') },
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  public onChangePersonalInfoClick(): void {
    this.#router.navigate(['edit'], { relativeTo: this.#activatedRoute });
  }

  public onDeleteAccountClick(): void {
    this.#modalService.create({
      nzContent: DeleteAccountComponent,
      nzWidth: 554,
      nzCentered: true,
      nzWrapClassName: 'modal-wrap'
    });
  }

  public onLanguageChange(langCode: 'en' | 'es'): void {
    let languageId: number;
    if (langCode === 'en') {
      languageId = 1;
    }
    if (langCode === 'es') {
      languageId = 2;
    }
    this.#usersService.updateUserLanguage(languageId).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
  }

  public onRemoveOkeeyCashAccount(): void {
    this.openRemoveOkeeyCaskAccountModal();
  }

  public openRemoveOkeeyCaskAccountModal(): void {
    this.#modalService
      .create({
        nzContent: RemoveAccountModalComponent,
        nzWrapClassName: 'modal-wrap',
        nzClosable: false,
        nzWidth: 555,
        nzClassName: 'modal-dialog'
      })
      .afterClose.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((removed: boolean) => {
        if (removed) {
          this.okeeyCashAccountRemoved.emit();
        }
      });
  }

  onAddTaxDocumentation(): void {
    this.openTaxDocumentationModal();
  }

  onTaxDocumentationDetailsClick(): void {
    this.openTaxDocumentationModal();
  }

  onRemoveTaxDocumentationClick(): void {
    const taxDocument = this.taxDocument();

    if (!taxDocument?.id || this.isTaxDocumentationLoading()) {
      return;
    }

    this.#modalService
      .create({
        nzContent: RemoveTaxDocumentationModalComponent,
        nzData: { taxDocument },
        nzWrapClassName: 'modal-wrap',
        nzClosable: false,
        nzWidth: 640,
        nzClassName: 'remove-tax-documentation-modal-dialog'
      })
      .afterClose.pipe(
        tap((removed: boolean) => {
          if (removed) {
            this.taxDocument.set(null);
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public onAddPaymentAccountClick(): void {
    this.#router.navigate(['payment-account', 'add'], { relativeTo: this.#activatedRoute });
  }

  public onEditPaymentAccountClick(): void {
    this.#router.navigate(['payment-account', 'summary'], { relativeTo: this.#activatedRoute });
  }

  private openTaxDocumentationModal(): void {
    this.#modalService
      .create({
        nzContent: TaxDocumentationModalComponent,
        nzWrapClassName: 'modal-wrap',
        nzClosable: false,
        nzWidth: 555,
        nzClassName: 'modal-dialog'
      })
      .afterClose.pipe(
        tap((result: boolean) => {
          if (result) {
            this.loadTaxDocumentation();
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }
  private loadTaxDocumentation(): void {
    this.isTaxDocumentationLoading.set(true);
    this.#paymentAccountsService
      .getMyTaxDocument()
      .pipe(
        finalize(() => this.isTaxDocumentationLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (response: any) => {
          this.taxDocument.set((response?.data ?? response) as TaxDocument);
        },
        error: (error: NetworkError) => {
          if (error?.code === 404) {
            this.taxDocument.set(null);
            return;
          }
        }
      });
  }

  private formatPhoneNumber(): string {
    const phone = this.user()?.fullPhoneNumber || '';

    return phone
      .replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3-$4')
      .replace(/^(\+52)(\d{2})(\d{4})(\d{4})$/, '$1 ($2) $3-$4');
  }
}
